import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Printer, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api';
import { TruckDiagram, DAMAGE_CODES, BODY_LABEL,
  type AxleCount, type BodyType } from '@/components/inspections/TruckDiagram';

/**
 * The inspection sheet, on paper.
 *
 * Not every inspection happens next to a screen. A truck in a yard at six in the
 * morning gets walked with a clipboard, and the paper has to carry the same
 * questions the app will ask later — so the checklist is FETCHED from the same
 * endpoint the on-screen form uses. Retyping it here would let the paper drift
 * from the record, and a form that asks different questions is worse than none.
 *
 * Printing is done by the browser: a `@media print` block hides the app and
 * leaves this sheet, so what comes out is what is on screen and nothing else.
 */

interface SheetItem { key: string; label: string }

/** The company block. Fixed here because a blank form is printed in batches and
 *  must not depend on a network call succeeding at the printer. */
const CO = {
  name: 'Jasr Alwadae Aldhahabi',
  cr: 'CR 7050077119',
  vat: 'VAT 313005567200003',
};

const TICKS = ['OK', 'Watch', 'Damaged', 'N/A'];

export function PrintableInspectionSheet({ onClose }: { onClose: () => void }) {
  const [axles, setAxles] = useState<AxleCount>(3);
  const [bodyType, setBodyType] = useState<BodyType>('tanker');

  const { data } = useQuery({
    queryKey: ['inspection-sheets'],
    // The endpoint nests the list under each sheet, alongside its interval.
    queryFn: async () => (await apiClient.get<{
      periodic: { intervalDays?: number; items: SheetItem[] };
      extraordinary: { items: SheetItem[] };
    }>('/api/v1/inspections/sheets')).data,
  });

  const periodic = data?.periodic?.items ?? [];
  const extra = data?.extraordinary?.items ?? [];
  const everyDays = data?.periodic?.intervalDays ?? 14;

  const Row = ({ label }: { label: string }) => (
    <tr>
      <td className="border border-black px-1.5 py-[2px] text-[8.5px] leading-tight">{label}</td>
      {TICKS.map((tk) => (
        <td key={tk} className="border border-black w-[26px] text-center align-middle">
          <span className="inline-block h-2.5 w-2.5 border border-black" />
        </td>
      ))}
    </tr>
  );

  const Table = ({ title, items }: { title: string; items: SheetItem[] }) => (
    <section className="break-inside-avoid">
      <h2 className="text-[9px] font-bold uppercase tracking-wide mb-0.5">{title}</h2>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="border border-black px-1.5 py-[2px] text-start text-[8px] uppercase">Item</th>
            {TICKS.map((tk) => (
              <th key={tk} className="border border-black px-0.5 py-[2px] text-[7px] uppercase w-[26px]">{tk}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length
            ? items.map((i) => <Row key={i.key} label={i.label} />)
            : <tr><td className="border border-black px-2 py-2 text-[11px]" colSpan={5}>
                Checklist could not be loaded — print again when connected.
              </td></tr>}
        </tbody>
      </table>
    </section>
  );

  const Field = ({ label, w = 'w-1/4' }: { label: string; w?: string }) => (
    <div className={`${w} px-1`}>
      <div className="text-[7.5px] uppercase tracking-wide">{label}</div>
      <div className="border-b border-black h-5" />
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto print:bg-white print:static print:overflow-visible"
         role="dialog" aria-modal="true">
      {/* Controls — never printed. */}
      <div className="sticky top-0 flex items-center justify-between gap-3 bg-card border-b border-border px-4 py-3 print:hidden">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium">Blank inspection sheet</span>
          <select value={bodyType} onChange={(e) => setBodyType(e.target.value as BodyType)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            {(Object.keys(BODY_LABEL) as BodyType[]).map((b) => (
              <option key={b} value={b}>{BODY_LABEL[b]}</option>
            ))}
          </select>
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            {([2, 3] as AxleCount[]).map((a) => (
              <button key={a} onClick={() => setAxles(a)}
                className={`px-3 h-9 text-sm ${axles === a ? 'bg-muted font-medium' : 'text-muted-foreground'}`}>
                {a} axles
              </button>
            ))}
          </div>
          <span className="text-xs text-muted-foreground">
            {axles === 2 ? 'rigid — 8 tyres at the back' : 'tractor and trailer — 12 tyres in all'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="gold" className="gap-2" onClick={() => window.print()}>
            <Printer className="h-4 w-4" /> Print
          </Button>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* The sheet itself. */}
      <div className="mx-auto my-6 bg-white text-black shadow-lg print:shadow-none print:my-0
                      w-[210mm] min-h-[297mm] p-[12mm] print:w-auto print:p-0"
           id="jasr-print-sheet">
        {/* letterhead */}
        <header className="flex items-start justify-between border-b-2 border-black pb-2">
          <div className="flex items-center gap-3">
            {/* 14 KB instead of 452 KB: the full logo is 1563px square, and a
                printer re-rasterises every one of those pixels for a 54px slot. */}
            <img src="/logo-print.png" alt="" width={54} height={54} />
            <div>
              <div className="text-[15px] font-bold leading-tight">{CO.name}</div>
              <div className="text-[9px]">{CO.cr} &nbsp; {CO.vat}</div>
            </div>
          </div>
          <div className="text-end">
            <div className="text-[14px] font-bold uppercase tracking-wide">Vehicle inspection</div>
            <div className="text-[9px]">Periodic / Extraordinary — tick one</div>
          </div>
        </header>

        {/* who and what */}
        <section className="mt-2">
          <div className="flex -mx-1">
            <Field label="Vehicle plate" /><Field label="Vehicle code" />
            <Field label="Odometer km" /><Field label="Date" />
          </div>
          <div className="flex -mx-1 mt-2">
            <Field label="Driver" w="w-1/3" /><Field label="Iqama number" w="w-1/3" />
            <Field label="Inspector" w="w-1/3" />
          </div>
          <div className="flex -mx-1 mt-2 items-end">
            <div className="w-1/2 px-1 text-[10px]">
              Type: <span className="inline-block h-3 w-3 border border-black align-middle mx-1" /> Periodic
              <span className="inline-block h-3 w-3 border border-black align-middle mx-1 ms-4" /> Extraordinary
            </div>
            <Field label="Body type / axles" w="w-1/2" />
          </div>
        </section>

        {/* the drawing */}
        <section className="mt-2 break-inside-avoid">
          <h2 className="text-[9px] font-bold uppercase tracking-wide mb-0.5">
            Mark the damage on the drawing
          </h2>
          <div className="border border-black p-1">
            <TruckDiagram states={{}} onToggle={() => {}} axles={axles} bodyType={bodyType} readOnly />
          </div>
          <div className="text-[9px] mt-1">
            <b>Codes:</b>{' '}
            {DAMAGE_CODES.map((d) => `${d.code} = ${d.label}`).join('   ·   ')}
          </div>
        </section>

        <div className="mt-3 grid grid-cols-2 gap-3 items-start">
          <Table title={`Periodic — every ${everyDays} days`} items={periodic} />
          <Table title="Extraordinary — after any change" items={extra} />
        </div>

        {/* what changed */}
        <section className="mt-2">
          <h2 className="text-[9px] font-bold uppercase tracking-wide mb-0.5">What was changed, and notes</h2>
          {[0, 1].map((i) => <div key={i} className="border-b border-black h-5" />)}
        </section>

        {/* signatures */}
        <section className="mt-3 flex gap-10">
          {['Inspector', 'Supervisor'].map((who) => (
            <div key={who} className="flex-1">
              <div className="border-b border-black h-6" />
              <div className="text-[10px] mt-1">{who} — name, signature and date</div>
            </div>
          ))}
        </section>

        <footer className="mt-2 pt-1 border-t border-black text-[7px] flex justify-between">
          <span>{CO.name}</span>
          <span>A change to the vehicle makes a periodic inspection due immediately.</span>
        </footer>
      </div>

      {/* Print rules: the sheet, and nothing else. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          /* The application is a sibling of this sheet now, so it goes in one
             rule rather than a visibility pass over every element in it. */
          #root { display: none !important; }
          #jasr-print-sheet {
            position: static; width: 100%; box-shadow: none;
            margin: 0; padding: 0; font-size: 9px;
          }
          .break-inside-avoid { break-inside: avoid; }
        }
      `}</style>
    </div>,
    document.body,
  );
}
